import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useMutation } from "react-query";
import { LoadingButton } from "@mui/lab";
import { ApplicationEventSubscription, ApplicationEventSubscriptionsService, Branch } from "gen/clients";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { describeApplicationEventType } from "utils/applicationEventUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  subscription: ApplicationEventSubscription;
  branches: Branch[];
  onSuccess: () => void;
}

enum FieldName {
  BRANCH_ID = "branchId",
  EMAILS = "emails",
  NOTES = "notes"
}

interface FormValues {
  [FieldName.BRANCH_ID]: string;
  [FieldName.EMAILS]: string;
  [FieldName.NOTES]: string;
}

const validationSchema = Yup.object({
  [FieldName.BRANCH_ID]: Yup.string().required("Branch is required"),
  [FieldName.EMAILS]: Yup.string()
    .required("Emails are required")
    .test("valid-emails", "Please enter valid comma-separated email addresses", value => {
      if (!value) {
        return false;
      }
      const emailList = value
        .split(",")
        .map(e => e.trim())
        .filter(e => e.length > 0);
      if (emailList.length === 0) {
        return false;
      }
      const emailRegex = /^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/;
      return emailList.every(email => emailRegex.test(email));
    }),
  [FieldName.NOTES]: Yup.string()
});

const EditEventSubscriptionDialog: React.FC<Props> = ({ open, onClose, subscription, branches, onSuccess }) => {
  const branchOptions = React.useMemo(() => {
    const options = branches
      .map(branch => ({
        label: branch.name,
        value: branch.id
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Add "All" option at the beginning
    return [{ label: "All", value: "ALL" }, ...options];
  }, [branches]);

  const updateMutation = useMutation(
    (values: FormValues) => {
      const emailList = values[FieldName.EMAILS]
        .split(",")
        .map(e => e.trim())
        .filter(e => e.length > 0);

      return ApplicationEventSubscriptionsService.updateApplicationEventSubscription({
        subscriptionId: subscription.id,
        requestBody: {
          emails: emailList,
          notes: values[FieldName.NOTES] || undefined
        }
      });
    },
    {
      onSuccess: () => {
        onSuccess();
        onClose();
      }
    }
  );

  const initialValues: FormValues = {
    [FieldName.BRANCH_ID]: subscription.branchId,
    [FieldName.EMAILS]: subscription.emails.join(", "),
    [FieldName.NOTES]: subscription.notes || ""
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Event Subscription</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={values => updateMutation.mutate(values)}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {updateMutation.error && <ApiErrorMessage apiError={updateMutation.error} />}

                <TextInputField
                  name="eventType"
                  label="Event Type"
                  value={describeApplicationEventType(subscription.eventType)}
                  disabled
                  helperText="Event type cannot be changed"
                />

                <SelectOneField
                  name={FieldName.BRANCH_ID}
                  label="Branch"
                  options={branchOptions}
                  required
                  disabled
                  helperText="Branch cannot be changed"
                />

                <TextInputField
                  name={FieldName.EMAILS}
                  label="Notification Emails"
                  required
                  helperText="Enter comma-separated email addresses"
                />

                <TextInputField name={FieldName.NOTES} label="Notes" />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                Save
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { EditEventSubscriptionDialog };
